import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import type { TicketDto, VoteDto, VoteValue } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';

function voterLabel(v: VoteDto): string {
  if (v.voterName) return v.voterName;
  if (v.voterPhone) return `…${v.voterPhone.slice(-4)}`;
  return v.userId.slice(0, 6);
}

export default function VoteWidget({ ticket }: { ticket: TicketDto }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [modalValue, setModalValue] = useState<VoteValue | null>(null);
  const [reason, setReason] = useState('');

  const votes = ticket.votes ?? [];
  const myVote = user ? votes.find((v) => v.userId === user.id) ?? null : null;
  const isCreator = user?.id === ticket.userId;
  const isVotable =
    ticket.status === 'NEEDS_REVIEW' || ticket.status === 'READY_FOR_SUBMISSION';

  useEffect(() => {
    if (modalValue) setReason('');
  }, [modalValue]);

  const cast = useMutation({
    mutationFn: (input: { value: VoteValue; reason: string }) =>
      api<{ ticket: TicketDto }>(`/tickets/${ticket.id}/vote`, {
        method: 'POST',
        json: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id, 'feed'] });
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id, 'audit'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Vote recorded');
      setModalValue(null);
      setReason('');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  if (isCreator || !isVotable) {
    if (votes.length === 0) return null;
    return (
      <div className="pt-4 border-t border-zinc-800">
        <div className="text-sm text-zinc-400 mb-2">Peer votes ({votes.length})</div>
        <VoteList votes={votes} />
      </div>
    );
  }

  const upCount = votes.filter((v) => v.value === 'UP').length;
  const downCount = votes.filter((v) => v.value === 'DOWN').length;

  const reasonTooShort = reason.trim().length < 20;
  const changingFrom = myVote && modalValue && myVote.value !== modalValue ? myVote.value : null;

  return (
    <div className="pt-4 border-t border-zinc-800 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-zinc-400">Peer review</div>
          <div className="text-xs text-zinc-500">
            {ticket.status === 'NEEDS_REVIEW'
              ? 'All peers must thumbs-up to advance to READY_FOR_SUBMISSION.'
              : 'All peers have approved. Anyone can submit.'}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <ThumbsUp size={14} /> {upCount}
          </span>
          <span className="inline-flex items-center gap-1 text-red-400">
            <ThumbsDown size={14} /> {downCount}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setModalValue('UP')}
          className={`btn-secondary ${myVote?.value === 'UP' ? 'ring-1 ring-emerald-500' : ''}`}
        >
          <ThumbsUp size={14} />
          {myVote?.value === 'UP' ? 'Your 👍 (update)' : 'Thumbs up'}
        </button>
        <button
          onClick={() => setModalValue('DOWN')}
          className={`btn-secondary ${myVote?.value === 'DOWN' ? 'ring-1 ring-red-500' : ''}`}
        >
          <ThumbsDown size={14} />
          {myVote?.value === 'DOWN' ? 'Your 👎 (update)' : 'Thumbs down'}
        </button>
      </div>

      {votes.length > 0 && <VoteList votes={votes} />}

      {modalValue && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalValue(null);
          }}
        >
          <div className="card max-w-lg w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold inline-flex items-center gap-2">
                {modalValue === 'UP' ? (
                  <ThumbsUp size={18} className="text-emerald-400" />
                ) : (
                  <ThumbsDown size={18} className="text-red-400" />
                )}
                {changingFrom
                  ? `Change vote ${changingFrom === 'UP' ? '👍' : '👎'} → ${
                      modalValue === 'UP' ? '👍' : '👎'
                    }`
                  : `Vote ${modalValue === 'UP' ? 'thumbs up' : 'thumbs down'}`}
              </div>
              <button
                onClick={() => setModalValue(null)}
                className="text-zinc-500 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="text-sm text-zinc-400">
              Explain your reasoning — at least 20 characters. This will be posted to the
              ticket's feed.
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full"
              placeholder={
                modalValue === 'UP'
                  ? 'Why are you approving this trade?'
                  : 'What concern blocks approval?'
              }
              autoFocus
            />
            <div className="flex items-center justify-between text-xs">
              <span
                className={reason.trim().length < 20 ? 'text-zinc-500' : 'text-emerald-400'}
              >
                {reason.trim().length} / 20 chars
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setModalValue(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={() => cast.mutate({ value: modalValue, reason: reason.trim() })}
                disabled={reasonTooShort || cast.isPending}
                className={modalValue === 'UP' ? 'btn-primary' : 'btn-danger'}
              >
                {cast.isPending
                  ? 'Submitting…'
                  : modalValue === 'UP'
                    ? 'Confirm 👍'
                    : 'Confirm 👎'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VoteList({ votes }: { votes: VoteDto[] }) {
  return (
    <ul className="space-y-1 text-xs">
      {votes.map((v) => (
        <li key={v.userId} className="flex items-center gap-2 text-zinc-400">
          {v.value === 'UP' ? (
            <ThumbsUp size={12} className="text-emerald-400 shrink-0" />
          ) : (
            <ThumbsDown size={12} className="text-red-400 shrink-0" />
          )}
          <span className="text-zinc-300">{voterLabel(v)}</span>
          <span className="truncate text-zinc-500">— {v.reason}</span>
        </li>
      ))}
    </ul>
  );
}
